import React, { useMemo, useState } from 'react';
import { Check, X, Lock, Users } from 'lucide-react';
interface Props {
  selectedThemes: string[];
  onViewConnections: () => void;
}
const ALL_PEOPLE = [{
  id: 1,
  theme: 'AI Governance',
  sponsor: 'EIIS',
  challenge: "Come si governa l'AI nella filiera alimentare?",
  photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face',
  traits: ['Lavora in Africa Occidentale', 'Framework regolatori']
}, {
  id: 2,
  theme: 'Climate Finance',
  sponsor: 'Barilla',
  challenge: 'Come finanziare la transizione energetica nei mercati emergenti?',
  photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300&h=300&fit=crop&crop=face',
  traits: ['Esperta di microfinanza', 'Sud-Est Asiatico']
}, {
  id: 3,
  theme: 'Supply Chain',
  sponsor: "McDonald's",
  challenge: 'Tracciabilità alimentare senza infrastruttura digitale?',
  photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face',
  traits: ['Startup founder', 'Blockchain per il food']
}, {
  id: 4,
  theme: 'Water Security',
  sponsor: 'EIIS',
  challenge: 'Governance idrica quando più paesi condividono lo stesso bacino?',
  photo: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=300&h=300&fit=crop&crop=face',
  traits: ['Policy advisor', 'Cooperazione internazionale']
}, {
  id: 5,
  theme: 'AI Governance',
  sponsor: 'Google',
  challenge: 'Bias algoritmico nelle decisioni sulla sicurezza alimentare?',
  photo: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=300&h=300&fit=crop&crop=face',
  traits: ['Data scientist', "Etica dell'AI"]
}];
export function MatchPeople({
  selectedThemes,
  onViewConnections
}: Props) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dragState, setDragState] = useState({
    isDragging: false,
    startX: 0,
    currentX: 0
  });
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [swipeDir, setSwipeDir] = useState<'left' | 'right' | null>(null);
  const [matchedCount, setMatchedCount] = useState(0);
  // Filter people based on selected themes
  const filteredPeople = useMemo(() => {
    if (selectedThemes.length === 0) return ALL_PEOPLE;
    return ALL_PEOPLE.filter(p => selectedThemes.includes(p.theme));
  }, [selectedThemes]);
  const allDone = currentIndex >= filteredPeople.length;
  const handleStart = (clientX: number) => {
    if (isAnimatingOut || allDone) return;
    setDragState({
      isDragging: true,
      startX: clientX,
      currentX: clientX
    });
  };
  const handleMove = (clientX: number) => {
    if (!dragState.isDragging || isAnimatingOut) return;
    setDragState(p => ({
      ...p,
      currentX: clientX
    }));
  };
  const handleEnd = () => {
    if (!dragState.isDragging || isAnimatingOut) return;
    const diff = dragState.currentX - dragState.startX;
    if (diff > 80) handleSwipe('right');else if (diff < -80) handleSwipe('left');else setDragState({
      isDragging: false,
      startX: 0,
      currentX: 0
    });
  };
  const handleSwipe = (dir: 'left' | 'right') => {
    setSwipeDir(dir);
    setIsAnimatingOut(true);
    setDragState({
      isDragging: false,
      startX: 0,
      currentX: 0
    });
    if (dir === 'right') setMatchedCount(p => p + 1);
    setTimeout(() => {
      setCurrentIndex(p => p + 1);
      setIsAnimatingOut(false);
      setSwipeDir(null);
    }, 300);
  };
  const diffX = dragState.isDragging ? dragState.currentX - dragState.startX : 0;
  const rotation = diffX * 0.04;
  const overlayOpacity = Math.min(Math.abs(diffX) / 100, 1);
  const transform = isAnimatingOut ? `translateX(${swipeDir === 'right' ? '150%' : '-150%'}) rotate(${swipeDir === 'right' ? '15deg' : '-15deg'})` : `translateX(${diffX}px) rotate(${rotation}deg)`;
  const transition = dragState.isDragging ? 'none' : 'transform 0.3s ease-out';
  if (allDone) {
    return <div className="flex-1 flex flex-col items-center justify-center text-center px-8 h-full pb-24" data-id="element-232">
        <div className="w-16 h-16 bg-[rgba(197,216,45,0.1)] rounded-full flex items-center justify-center mb-5" data-id="element-233">
          <Users className="w-8 h-8 text-[#C5D82D]" data-id="element-234" />
        </div>
        <h3 className="text-[16px] font-medium text-white mb-2 tracking-tight" data-id="element-235">
          {matchedCount > 0 ? `${matchedCount} richieste inviate!` : 'Nessuna richiesta inviata'}
        </h3>
        <p className="text-[13px] text-[#888888] leading-relaxed mb-6" data-id="element-236">
          {matchedCount > 0 ? 'Se anche loro accettano, scoprirai chi sono.' : 'Puoi cambiare i filtri per trovare altre persone.'}
        </p>
        <div className="flex gap-3" data-id="element-237">
          <button onClick={onViewConnections} className="px-5 py-2 rounded-full border border-[#C5D82D] bg-transparent text-[13px] text-white font-medium active:scale-95 transition-all" data-id="element-238">
            Vedi Connessioni
          </button>
        </div>
      </div>;
  }
  const person = filteredPeople[currentIndex];
  const next = currentIndex + 1 < filteredPeople.length ? filteredPeople[currentIndex + 1] : null;
  return <div className="flex-1 flex flex-col relative h-full px-6 pt-5 pb-24" data-id="element-239">
      {/* Progress */}
      <div className="flex gap-1.5 mb-5" data-id="element-240">
        {filteredPeople.map((_, i) => <div key={i} className={`h-1 rounded-full transition-all duration-300 ${i === currentIndex ? 'w-6 bg-[#C5D82D]' : i < currentIndex ? 'w-2 bg-[#C5D82D]/30' : 'w-2 bg-[#2A2A2A]'}`} data-id="element-241" />)}
      </div>

      {/* Card Stack */}
      <div className="flex-1 relative mb-5" data-id="element-242">
        {next && <div className="absolute inset-0 bg-[#141414] border border-[#2A2A2A]/40 rounded-xl transform translate-y-2 scale-[0.97] z-0" data-id="element-243" />}

        <div className="absolute inset-0 bg-[#141414] border border-[#2A2A2A]/60 rounded-xl z-10 flex flex-col overflow-hidden touch-none" style={{
        transform,
        transition,
        cursor: dragState.isDragging ? 'grabbing' : 'grab'
      }} onMouseDown={e => handleStart(e.clientX)} onMouseMove={e => handleMove(e.clientX)} onMouseUp={handleEnd} onMouseLeave={handleEnd} onTouchStart={e => handleStart(e.touches[0].clientX)} onTouchMove={e => handleMove(e.touches[0].clientX)} onTouchEnd={handleEnd} data-id="element-244">
          {/* Swipe Overlays */}
          <div className="absolute inset-0 bg-[#C5D82D]/15 z-20 flex items-center justify-center pointer-events-none" style={{
          opacity: diffX > 0 ? overlayOpacity : 0
        }} data-id="element-245">
            <div className="border-2 border-[#C5D82D] text-[#C5D82D] rounded-lg px-5 py-2 font-medium text-sm transform -rotate-12 bg-black/50 backdrop-blur-sm" data-id="element-246">
              VOGLIO PARLARE
            </div>
          </div>
          <div className="absolute inset-0 bg-[#D32F2F]/15 z-20 flex items-center justify-center pointer-events-none" style={{
          opacity: diffX < 0 ? overlayOpacity : 0
        }} data-id="element-247">
            <div className="border-2 border-[#D32F2F] text-[#D32F2F] rounded-lg px-5 py-2 font-medium text-sm transform rotate-12 bg-black/50 backdrop-blur-sm" data-id="element-248">
              PASSA
            </div>
          </div>

          {/* Person Card Content */}
          <div className="flex flex-col h-full pointer-events-none" data-id="element-249">
            {/* Top Section: Theme, Sponsor, Challenge */}
            <div className="p-6 flex flex-col gap-4" data-id="element-250">
              <div className="flex items-center justify-between gap-2" data-id="element-251">
                <span className="px-3 py-1 rounded-full bg-[#2A2A2A] text-white text-[11px] font-medium whitespace-nowrap" data-id="element-252">
                  {person.theme}
                </span>
                <span className={`px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap ${person.sponsor === 'EIIS' ? 'bg-[rgba(197,216,45,0.08)] text-[#C5D82D]' : 'bg-[rgba(255,143,0,0.12)] text-[#FF8F00]'}`} data-id="element-253">
                  Proposto da {person.sponsor}
                </span>
              </div>

              <h2 className="text-[16px] text-white font-medium leading-snug" data-id="element-254">
                {person.challenge}
              </h2>
            </div>

            <div className="w-full h-[1px] bg-[#2A2A2A]" data-id="element-255" />

            {/* Bottom Section: Blurred Photo & Traits */}
            <div className="flex-1 relative overflow-hidden bg-[#1A1A1A] flex flex-col" data-id="element-256">
              <div className="absolute inset-0" data-id="element-257">
                <img src={person.photo} alt="" className="w-full h-full object-cover scale-110 opacity-40" style={{
                filter: 'blur(16px)'
              }} data-id="element-258" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-[#141414]/80 to-transparent" data-id="element-259" />
              </div>

              <div className="relative z-10 flex flex-col items-center justify-end h-full px-6 pb-6 pt-10" data-id="element-260">
                {/* Blurred Name Lines */}
                <div className="space-y-2 mb-5 flex flex-col items-center" data-id="element-261">
                  <div className="h-4 bg-white/20 rounded-full w-36 blur-[3px]" data-id="element-262" />
                  <div className="h-3 bg-white/10 rounded-full w-24 blur-[3px]" data-id="element-263" />
                </div>

                <div className="w-8 h-[1px] bg-white/10 mb-5" data-id="element-264" />

                {/* Traits */}
                {person.traits.map((trait, i) => <p key={i} className="text-[13px] text-white/90 mb-1.5 text-center font-medium" data-id="element-265">
                    {trait}
                  </p>)}

                {/* Lock notice */}
                <div className="flex items-center gap-1.5 mt-6 bg-black/40 px-3 py-1.5 rounded-full backdrop-blur-md" data-id="element-266">
                  <Lock className="w-3 h-3 text-[#C5D82D]" data-id="element-267" />
                  <span className="text-[10px] text-[#C5D82D] font-medium" data-id="element-268">
                    Identità svelata dopo il match
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex items-center justify-center gap-8" data-id="element-269">
        <button onClick={() => handleSwipe('left')} className="w-12 h-12 rounded-full bg-[#141414] border border-[#2A2A2A]/60 flex items-center justify-center text-[#D32F2F] active:scale-95 transition-all" data-id="element-270">
          <X className="w-5 h-5" strokeWidth={2} data-id="element-271" />
        </button>
        <button onClick={() => handleSwipe('right')} className="w-12 h-12 rounded-full bg-[#141414] border border-[#2A2A2A]/60 flex items-center justify-center text-[#C5D82D] active:scale-95 transition-all" data-id="element-272">
          <Check className="w-5 h-5" strokeWidth={2} data-id="element-273" />
        </button>
      </div>
    </div>;
}